import React from 'react';
import {ForecastMapService} from '../api';
import {ForecastMapConfig} from '../api/types';
import useApiClient from './useApiClient';

export type ForecastMapCatalogStatus = 'loading' | 'ready' | 'error';

type CatalogResult = {
  // Which load produced this result; a mismatch means a load is in flight.
  key: number | null;
  status: 'ready' | 'error';
  configs: ForecastMapConfig[];
};

/**
 * Fetches the forecast-map preset catalog (all models — grouping happens
 * client-side so switching models never refetches).
 *
 * Platform-agnostic on purpose (no DOM usage): backs the webapp screens and
 * the mobile forecast-map screen.
 */
const useForecastMapCatalog = () => {
  const {fetch} = useApiClient();
  const [loadKey, setLoadKey] = React.useState(0);
  const [result, setResult] = React.useState<CatalogResult>({
    key: null,
    status: 'ready',
    configs: [],
  });

  React.useEffect(() => {
    let cancelled = false;
    fetch(ForecastMapService.forecastMapConfigList())
      .then((configs) => {
        if (!cancelled) {
          setResult({key: loadKey, status: 'ready', configs});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({key: loadKey, status: 'error', configs: []});
        }
      });
    // Drop late responses on unmount/reload.
    return () => {
      cancelled = true;
    };
  }, [fetch, loadKey]);

  const reload = React.useCallback(() => setLoadKey((key) => key + 1), []);

  // Derived: any load whose result hasn't landed yet renders as loading.
  const loading = result.key !== loadKey;
  const status: ForecastMapCatalogStatus = loading ? 'loading' : result.status;

  return {status, configs: loading ? [] : result.configs, reload};
};

export default useForecastMapCatalog;
